import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Shield, Eye, X, ChevronLeft, ChevronRight, Search, Filter } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

const ENTIDADE_LABEL = {
  usuarios: 'Usuário', pessoas: 'Pessoa', bancos: 'Banco',
  financeiros: 'Financeiro', acertos: 'Acerto de Motorista',
  entradas_saidas: 'Entrada/Saída', relatorios: 'Relatório',
};

const ACAO_BADGE = {
  criar:         'bg-green-100 text-green-700',
  criar_parcela: 'bg-green-100 text-green-700',
  editar:        'bg-blue-100 text-blue-700',
  atualizar:     'bg-blue-100 text-blue-700',
  atualizar_saldo: 'bg-blue-100 text-blue-700',
  renomear:      'bg-blue-100 text-blue-700',
  excluir:       'bg-red-100 text-red-700',
  inativar:      'bg-orange-100 text-orange-700',
  cancelar:      'bg-red-100 text-red-700',
  quitar:        'bg-purple-100 text-purple-700',
  gerar_recibo:  'bg-cyan-100 text-cyan-700',
  login:         'bg-slate-100 text-slate-600',
};

const ACAO_LABEL = {
  criar: 'Criar', criar_parcela: 'Criar Parcela', editar: 'Editar',
  atualizar: 'Editar', atualizar_saldo: 'Atualizar Saldo', renomear: 'Renomear',
  excluir: 'Excluir', inativar: 'Inativar', cancelar: 'Cancelar',
  quitar: 'Quitar', gerar_recibo: 'Recibo', login: 'Login',
};

const ENTIDADES = ['usuarios', 'pessoas', 'bancos', 'financeiros', 'acertos'];
const ACOES = ['criar', 'editar', 'atualizar', 'excluir', 'inativar', 'cancelar', 'quitar', 'gerar_recibo', 'login', 'criar_parcela'];

const Auditoria = () => {
  const [dados, setDados] = useState({ total: 0, pagina: 1, por_pagina: 50, registros: [] });
  const [loading, setLoading] = useState(true);
  const [detalhe, setDetalhe] = useState(null);
  const [filtros, setFiltros] = useState({
    entidade: '', acao: '', busca: '', data_inicio: '', data_fim: '',
  });
  const [pagina, setPagina] = useState(1);

  const carregar = useCallback(async (pag = 1, f = filtros) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ pagina: pag, por_pagina: 50 });
      if (f.entidade) params.append('entidade', f.entidade);
      if (f.acao) params.append('acao', f.acao);
      if (f.busca) params.append('busca', f.busca);
      if (f.data_inicio) params.append('data_inicio', f.data_inicio);
      if (f.data_fim) params.append('data_fim', f.data_fim);
      const { data } = await api.get(`/auditorias?${params}`);
      setDados(data);
      setPagina(pag);
    } catch {
      toast.error('Erro ao carregar auditoria');
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => { carregar(1); }, []); // eslint-disable-line

  const aplicarFiltros = (e) => { e.preventDefault(); carregar(1, filtros); };
  const limparFiltros = () => {
    const vazio = { entidade: '', acao: '', busca: '', data_inicio: '', data_fim: '' };
    setFiltros(vazio);
    carregar(1, vazio);
  };

  const totalPaginas = Math.ceil(dados.total / dados.por_pagina) || 1;

  const formatDate = (d) => new Date(d).toLocaleString('pt-BR');

  const badgeClass = (acao) => ACAO_BADGE[acao] || 'bg-slate-100 text-slate-700';
  const acaoLabel = (acao) => ACAO_LABEL[acao] || acao.replace(/_/g, ' ');

  return (
    <div className="space-y-6 animate-slide-up" data-testid="auditoria-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-1 flex items-center gap-3">
          <Shield className="w-8 h-8 text-blue-600" /> Auditoria
        </h1>
        <p className="text-slate-500 text-sm">Registro de todas as ações realizadas no sistema</p>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={aplicarFiltros} className="flex flex-wrap gap-3 items-end">
            <div className="flex flex-col gap-1 min-w-[180px]">
              <label className="text-xs font-medium text-slate-500 flex items-center gap-1"><Search className="w-3 h-3" /> Busca</label>
              <input
                value={filtros.busca}
                onChange={e => setFiltros(f => ({ ...f, busca: e.target.value }))}
                placeholder="Nome, descrição..."
                className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-slate-500 flex items-center gap-1"><Filter className="w-3 h-3" /> Entidade</label>
              <select
                value={filtros.entidade}
                onChange={e => setFiltros(f => ({ ...f, entidade: e.target.value }))}
                className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todas</option>
                {ENTIDADES.map(e => <option key={e} value={e}>{ENTIDADE_LABEL[e] || e}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-slate-500">Ação</label>
              <select
                value={filtros.acao}
                onChange={e => setFiltros(f => ({ ...f, acao: e.target.value }))}
                className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todas</option>
                {ACOES.map(a => <option key={a} value={a}>{ACAO_LABEL[a] || a}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-slate-500">Data início</label>
              <input type="date" value={filtros.data_inicio}
                onChange={e => setFiltros(f => ({ ...f, data_inicio: e.target.value }))}
                className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-slate-500">Data fim</label>
              <input type="date" value={filtros.data_fim}
                onChange={e => setFiltros(f => ({ ...f, data_fim: e.target.value }))}
                className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700">
              Filtrar
            </button>
            <button type="button" onClick={limparFiltros} className="px-4 py-2 border border-slate-200 text-slate-600 text-sm font-medium rounded-md hover:bg-slate-50">
              Limpar
            </button>
          </form>
        </CardContent>
      </Card>

      {/* Contagem */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          {loading ? 'Carregando...' : `${dados.total} registro${dados.total !== 1 ? 's' : ''} encontrado${dados.total !== 1 ? 's' : ''}`}
        </p>
        {totalPaginas > 1 && (
          <div className="flex items-center gap-2 text-sm">
            <button onClick={() => carregar(pagina - 1)} disabled={pagina <= 1}
              className="p-1 rounded hover:bg-slate-100 disabled:opacity-40">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-slate-600">Página {pagina} de {totalPaginas}</span>
            <button onClick={() => carregar(pagina + 1)} disabled={pagina >= totalPaginas}
              className="p-1 rounded hover:bg-slate-100 disabled:opacity-40">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Lista */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
        </div>
      ) : dados.registros.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Shield className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Nenhum registro encontrado</p>
        </div>
      ) : (
        <div className="space-y-2">
          {dados.registros.map((audit) => (
            <Card key={audit.id} className="card-hover">
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {/* Badge ação */}
                    <span className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${badgeClass(audit.acao)}`}>
                      {acaoLabel(audit.acao)}
                    </span>
                    {/* Descrição */}
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">
                        {audit.descricao || `${audit.acao} • ${ENTIDADE_LABEL[audit.entidade] || audit.entidade}`}
                      </p>
                      <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                        <span className="text-xs text-slate-500">
                          👤 {audit.usuario_nome || audit.usuario_id.slice(0, 8)}
                        </span>
                        <span className="text-xs text-slate-400">
                          🕐 {formatDate(audit.criado_em)}
                        </span>
                        {audit.ip && (
                          <span className="text-xs text-slate-400">IP: {audit.ip}</span>
                        )}
                        <span className="text-xs text-slate-300">#{audit.entidade_id.slice(0, 8).toUpperCase()}</span>
                      </div>
                    </div>
                  </div>
                  {(audit.dados_antes || audit.dados_depois) && (
                    <button
                      onClick={() => setDetalhe(audit)}
                      className="shrink-0 text-blue-500 hover:text-blue-700 p-1.5 rounded hover:bg-blue-50"
                      title="Ver detalhes"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Paginação inferior */}
      {totalPaginas > 1 && !loading && (
        <div className="flex justify-center gap-2 pt-2">
          <button onClick={() => carregar(pagina - 1)} disabled={pagina <= 1}
            className="flex items-center gap-1 px-3 py-1.5 border border-slate-200 rounded text-sm hover:bg-slate-50 disabled:opacity-40">
            <ChevronLeft className="w-4 h-4" /> Anterior
          </button>
          <span className="flex items-center px-3 text-sm text-slate-600">
            {pagina} / {totalPaginas}
          </span>
          <button onClick={() => carregar(pagina + 1)} disabled={pagina >= totalPaginas}
            className="flex items-center gap-1 px-3 py-1.5 border border-slate-200 rounded text-sm hover:bg-slate-50 disabled:opacity-40">
            Próximo <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Modal detalhes */}
      {detalhe && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setDetalhe(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div>
                <h2 className="font-bold text-slate-900">Detalhes da Ação</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {detalhe.descricao} — {formatDate(detalhe.criado_em)}
                </p>
              </div>
              <button onClick={() => setDetalhe(null)} className="p-1.5 rounded hover:bg-slate-100">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="overflow-auto flex-1 p-5">
              {detalhe.dados_antes && detalhe.dados_depois ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-bold text-orange-600 uppercase mb-2">Antes</p>
                    <DiffTable dados={detalhe.dados_antes} outro={detalhe.dados_depois} lado="antes" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-green-600 uppercase mb-2">Depois</p>
                    <DiffTable dados={detalhe.dados_depois} outro={detalhe.dados_antes} lado="depois" />
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase mb-2">
                    {detalhe.dados_antes ? 'Dados removidos' : 'Dados criados'}
                  </p>
                  <DiffTable dados={detalhe.dados_antes || detalhe.dados_depois} outro={{}} lado="unico" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CAMPOS_IGNORAR = ['id', 'criado_em', 'atualizado_em', 'senha_hash', '_id'];
const CAMPO_LABEL = {
  nome: 'Nome', email: 'Email', role: 'Perfil', ativo: 'Ativo',
  descricao: 'Descrição', valor: 'Valor', status: 'Status',
  data_vencimento: 'Vencimento', data_pagamento: 'Pagamento',
  nome_pessoa: 'Pessoa', banco_nome: 'Banco', tipo: 'Tipo',
  motorista_nome: 'Motorista', data_inicio: 'Início', data_fim: 'Fim',
  percentual_motorista: '% Motorista', total_motorista: 'Total Motorista',
  total_empresa: 'Total Empresa', total_geral: 'Total Geral',
  saldo_inicial: 'Saldo Inicial', saldo_atual: 'Saldo Atual',
  observacoes: 'Observações',
};

const DiffTable = ({ dados, outro, lado }) => {
  if (!dados) return null;
  const entradas = Object.entries(dados).filter(([k]) => !CAMPOS_IGNORAR.includes(k));
  return (
    <table className="w-full text-xs border-collapse">
      <tbody>
        {entradas.map(([k, v]) => {
          const mudou = lado !== 'unico' && JSON.stringify(v) !== JSON.stringify(outro?.[k]);
          const label = CAMPO_LABEL[k] || k.replace(/_/g, ' ');
          const val = typeof v === 'object' ? JSON.stringify(v) : String(v ?? '—');
          return (
            <tr key={k} className={mudou ? (lado === 'depois' ? 'bg-green-50' : 'bg-orange-50') : ''}>
              <td className="py-1 pr-3 font-medium text-slate-500 whitespace-nowrap">{label}</td>
              <td className={`py-1 break-all ${mudou ? (lado === 'depois' ? 'text-green-700 font-semibold' : 'text-orange-700 line-through opacity-60') : 'text-slate-700'}`}>
                {val}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default Auditoria;
